import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { T } from "@/lib/i18n";
import { Crop, X } from "lucide-react";

const ASPECT = 3 / 4;

function loadImage(src) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
}

async function cropToBlob(src, area) {
  const img = await loadImage(src);
  const canvas = document.createElement("canvas");
  const outW = 600, outH = 800;
  canvas.width = outW;
  canvas.height = outH;
  canvas.getContext("2d").drawImage(img, area.x, area.y, area.width, area.height, 0, 0, outW, outH);
  return new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.9));
}

export const PhotoCropDialog = ({ src, lang, onCancel, onDone }) => {
  const t = T[lang];
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);
  const onComplete = useCallback((_, pixels) => setArea(pixels), []);

  const confirm = async () => {
    if (!area) return;
    setBusy(true);
    const blob = await cropToBlob(src, area);
    onDone(new File([blob], "photo.jpg", { type: "image/jpeg" }));
  };

  return (
    <Dialog open={!!src} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent data-testid="crop-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-[#1a2e4a] flex items-center gap-2"><Crop className="h-5 w-5 text-[#c0392b]" /> {t.cropTitle}</DialogTitle>
          <DialogDescription className="text-[#64748b]">{t.cropDesc}</DialogDescription>
        </DialogHeader>
        <div data-testid="crop-area" className="relative w-full h-[340px] bg-[#0f1c2e] rounded-lg overflow-hidden">
          {src && (
            <Cropper image={src} crop={crop} zoom={zoom} aspect={ASPECT} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} showGrid={false} />
          )}
        </div>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-[#64748b] w-20">{t.zoom}</span>
          <Slider data-testid="crop-zoom" value={[zoom]} min={1} max={3} step={0.05} onValueChange={(v) => setZoom(v[0])} className="flex-1" />
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button data-testid="crop-cancel-btn" variant="outline" onClick={onCancel} className="border-[#1a2e4a] text-[#1a2e4a]"><X className="h-4 w-4 mr-1" /> {t.cancel}</Button>
          <Button data-testid="crop-save-btn" disabled={busy || !area} onClick={confirm} className="bg-[#c0392b] hover:bg-[#a93226] text-white"><Crop className="h-4 w-4 mr-1" /> {busy ? t.uploading : t.cropSave}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
